import { createSelector } from "reselect";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import { isDepositEnabled } from "app/pages/.shared/Deposit/depositSelector";
import { getHoursCount } from "app/utils/utils";
import {
	getAllCouponsTotal,
	getAvailableCreditsTotal,
	getCouponType,
	getPromotionAmount,
	getPromotionCode,
} from "app/pages/Account/MyCoupons/couponSelector";
import { getSelectedActivitiesForPriceDetails } from "app/pages/Booking/Quotation/Activity/QuotationActivitySelectors";
import { calculateFlexIncluded } from "app/utils/quotationUtils";
import get from "lodash/get";
import { getDistributions, getQuotation } from "app/pages/Booking/Quotation/quotationSelectors";
import {
	BAGGAGE_TYPES,
	OFFER_TYPES,
	PAYMENT_COUPON_TYPE,
	TRANSPORTATION_TYPES,
} from "app/constants";

export const getOffer = state => state.booking.offer;
const getAccommodation = state => state.booking.accommodation;
export const getDestinationResort = state => state.booking.destinationResort;
const getTransfer = state => state.booking.transfer;
const getBoard = state => state.booking.board;
const getBoardByDistribution = state => state.booking.boardByDistribution;
export const getFlight = state => state.booking.flight;
const getFlightInbound = state => getFlight(state)?.inbound;
const getFlightOutbound = state => getFlight(state)?.outbound;
const getFlightInboundAirline = state => {
	const flightInbound = getFlightInbound(state);

	return flightInbound && flightInbound.airline;
};
export const getFlightInboundAirlineCode = state => {
	const flightInboundAirline = getFlightInboundAirline(state);

	return flightInboundAirline ? flightInboundAirline.code : "";
};
const getInsurance = state => state.booking.insurance;
export const getInsuranceFlex = state => state.booking.insuranceFlex;
export const getPassengers = state => state.booking.passengers;
export const getHideDiscountHBX = state => state.brand?.hideDiscountHBX;
const getLuggage = state => state.booking.luggage;
const getCabinLuggage = state => state.booking.cabinLuggage;
const getQuotationItems = state => state.quotation.quotationItems;
export const getPaymentDeltaBasePrice = state => state?.payment?.deltaBasePrice;
const getAdults = state => state.booking.adults;
const getChildren = state => state.booking.children;
const getBabies = state => state.booking.infants;
const getOccupancies = state => state.booking.occupancies;
export const getBookingDepartureDate = state => state.booking.departureDate;
export const getSelectedStopoverCityId = state => state.booking.stopoverCityId;
export const getSelectedStopoverDuration = state => state.booking.stopoverDuration;
const getBooking = state => state?.booking || {};

export const getHasSpecialOffer = state => state.booking.hasSpecialOffer;

export const isBookingEligibleForDeposit = createSelector(
	[isDepositEnabled, getBookingDepartureDate],
	(isDepositEnabled, departureDate) => {
		if (isDepositEnabled && departureDate) {
			const bookingDateISO = new Date().toISOString();
			const departureDateISO = new Date(departureDate).toISOString();
			return getHoursCount(bookingDateISO, departureDateISO) / 24 > 30;
		}
		return false;
	}
);
export const isBookingDateEligibleForDeposit = createSelector(
	[getBookingDepartureDate],
	departureDate => {
		if (departureDate) {
			const bookingDateISO = new Date().toISOString();
			const departureDateISO = new Date(departureDate).toISOString();
			return getHoursCount(bookingDateISO, departureDateISO) / 24 > 30;
		}
		return true;
	}
);

export const getBookingOfferType = createSelector([getOffer], (offer = {}) => {
	return offer.type;
});

export const getBookingFeesAmount = createSelector([getQuotationItems], (quotationItem = []) => {
	const bookingFeesItem = find(quotationItem, item => item.type === "BOOKING_FEES");
	return bookingFeesItem ? bookingFeesItem.price : 0;
});

export const getRoomUpgrade = createSelector([getAccommodation], (accommodation = {}) => {
	return !isEmpty(accommodation) && !accommodation.included ? accommodation : undefined;
});

export const getBoardUpgrade = createSelector([getBoard], (board = {}) => {
	return !isEmpty(board) && board.upgradePrice !== 0 ? board : undefined;
});

export const getFlightUpgrade = createSelector([getFlight], (flight = {}) => {
	return !isEmpty(flight) && !flight.included ? flight : undefined;
});

export const getTransferUpgrade = createSelector([getTransfer], (transfer = {}) => {
	return !isEmpty(transfer) && !transfer.included ? transfer : undefined;
});

export const getFlightOptionsList = createSelector([getQuotation], (quotation = {}) => {
	return quotation.flightOptions || [];
});

export const getFlightOptionsOfSelectedFlight = createSelector(
	[getFlight, getQuotation],
	(flight = {}, quotation = {}) => {
		if (flight?.code && !isEmpty(quotation?.flightOptions)) {
			return quotation.flightOptions[flight?.code] || {};
		}
		return {};
	}
);

export const getCurrentLuggageOption = createSelector(
	[getFlightOptionsOfSelectedFlight, getLuggage],
	(flightOptions = {}, luggage = {}) => {
		if (flightOptions?.flightBagageOptions && luggage?.quantity && luggage?.quantity >= 1) {
			const selectedBagageOption = flightOptions.flightBagageOptions.find(
				flightBagageOption =>
					flightBagageOption?.quantity === luggage?.quantity &&
					flightBagageOption?.maxWeightPerBaggage === luggage?.maxWeightPerBaggage
			);

			return selectedBagageOption || {};
		}

		if (flightOptions?.flightBagageOption) {
			return flightOptions.flightBagageOption;
		}

		return {};
	}
);

export const getLuggageTotalPrice = createSelector(
	[getLuggage, getFlightOptionsOfSelectedFlight],
	(luggage = {}, flightOptions = {}) => {
		const flightBagageOptions = flightOptions?.flightBagageOptions;

		if (luggage === undefined || luggage === 0) {
			return 0;
		} else if (luggage?.quantity >= 1 && flightBagageOptions) {
			const selectedBagageOption = flightBagageOptions.find(
				flightBagageOption =>
					flightBagageOption.quantity === luggage?.quantity &&
					flightBagageOption.maxWeightPerBaggage === luggage?.maxWeightPerBaggage
			);
			return selectedBagageOption ? selectedBagageOption.pricePerBooking : 0;
		}
		return 0;
	}
);

export const getLuggageTotalPublicPrice = createSelector(
	[getLuggage, getFlightOptionsOfSelectedFlight],
	(luggage = {}, flightOptions = {}) => {
		const flightBagageOptions = flightOptions?.flightBagageOptions;

		if (luggage === undefined || luggage === 0) {
			return 0;
		} else if (luggage?.quantity >= 1 && flightBagageOptions) {
			const selectedBagageOption = flightBagageOptions.find(
				flightBagageOption =>
					flightBagageOption.quantity === luggage?.quantity &&
					flightBagageOption.maxWeightPerBaggage === luggage?.maxWeightPerBaggage
			);
			return selectedBagageOption ? selectedBagageOption.publicPricePerBooking : 0;
		}
		return 0;
	}
);

export const getCabinLuggageTotalPrice = createSelector(
	[getCabinLuggage, getFlightOptionsOfSelectedFlight],
	(cabinLuggage = {}, flightOptions = {}) => {
		const mainFlightBaggageOption = flightOptions?.flightBagageOption;

		if (!cabinLuggage || !cabinLuggage.quantity || cabinLuggage.quantity === 0) {
			return 0;
		} else if (cabinLuggage?.quantity >= 1 && mainFlightBaggageOption) {
			const selectedBagageOption = mainFlightBaggageOption.cabinBaggages?.find(
				cabinBaggage =>
					cabinBaggage.quantity === cabinLuggage?.quantity &&
					cabinBaggage.maxWeightPerBaggage === cabinLuggage?.maxWeightPerBaggage
			);
			return selectedBagageOption ? selectedBagageOption.pricePerBooking : 0;
		}

		return 0;
	}
);

export const calculateActivitiesUpgradePrice = createSelector(
	[getSelectedActivitiesForPriceDetails],
	(activities = []) => {
		return activities.reduce((acc, activity) => {
			return (
				acc +
				activity.guests.reduce((guestAcc, guest) => {
					const guestCount = guest.guestsCount;

					const guestPrices = guest.pricePerQuantity[guestCount] || {};
					const guestPrice = guestPrices.price || 0;
					const guestDiscount = guestPrices.discount || 0;

					return guestAcc + guestPrice + guestDiscount;
				}, 0)
			);
		}, 0);
	}
);

export const getAccommodationsPrices = createSelector(
	[getDistributions, getBooking],
	(distributions, booking = {}) => {
		const selectedDistributions = Object.keys(booking?.boardIdsByDistribution || {});

		return selectedDistributions.map(distributionId => {
			const distribution = distributions.find(({ id }) => id === distributionId);
			let boardWithPrice = {};

			// eslint-disable-next-line no-unused-expressions
			distribution?.rooms?.forEach(({ boards }) => {
				const board = boards.find(
					({ id }) => id === booking?.boardIdsByDistribution[distributionId]
				);
				if (board) {
					boardWithPrice = board;
				}
			});

			return boardWithPrice;
		});
	}
);

export const getTotalExcludedTaxes = createSelector(
	[getAccommodationsPrices],
	accommodationsPrices => {
		return accommodationsPrices.reduce(
			(total, price) => total + (price?.totalExcludedTaxes || 0),
			0
		);
	}
);

export const getAccommodationsTotalPrice = createSelector(
	[getAccommodationsPrices],
	accommodationsPrices => {
		return accommodationsPrices.reduce(
			(total, price) => total + price?.finalPricePerStayWithoutTaxes,
			0
		);
	}
);

/**
 * Calcule le total du panier de le page quote en tenant compte de :
 * - supplement chambre
 * - supplement pension
 * - supplement vol
 * - supplement transfert
 * - supplement activites
 * - supplement bagages
 * - supplement bagages cabine
 * C'est le total utilisé pour calculer le supplément flex et le total envoyé à la requete de prebook
 */
export const calculateQuoteBaseAndExtrasTotal = createSelector(
	[
		getQuotationItems,
		getRoomUpgrade,
		getBoardUpgrade,
		getFlightUpgrade,
		getTransferUpgrade,
		calculateActivitiesUpgradePrice,
		getLuggageTotalPrice,
		getCabinLuggageTotalPrice,
		getAccommodationsTotalPrice,
	],
	(
		quotationItems = [],
		roomUpgrade = {},
		boardUpgrade = {},
		flightUpgrade = {},
		transferUpgrade = {},
		activitiesUpgradePrice = 0,
		luggageTotalPrice = 0,
		cabinLuggageTotalPrice = 0,
		accommodationsTotalPrice = 0
	) => {
		const prices = quotationItems.map(quotationItem => {
			return quotationItem.price;
		});

		prices.push(boardUpgrade.upgradePrice || 0);
		prices.push(flightUpgrade.upgradePrice || 0);
		prices.push(accommodationsTotalPrice);
		prices.push(roomUpgrade.upgradePrice || 0);
		prices.push(activitiesUpgradePrice);
		prices.push(transferUpgrade.upgradePrice || 0);
		prices.push(luggageTotalPrice);
		prices.push(cabinLuggageTotalPrice);

		const subtotal = prices.reduce((previousPrice, price) => {
			return previousPrice + price;
		});
		return subtotal;
	}
);

export const calculateFlexExtra = createSelector(
	[calculateQuoteBaseAndExtrasTotal, getInsuranceFlex, getPassengers],
	(total = 0, insuranceFlex = undefined, passengers = []) => {
		const nbPassengers = passengers.length;
		const flex = calculateFlexIncluded(insuranceFlex, total, nbPassengers);
		return flex;
	}
);

export const calculateCreditBasedOnQuoteBase = createSelector(
	[calculateQuoteBaseAndExtrasTotal, getAvailableCreditsTotal],
	(total = 0, creditsAvailable = 0) => {
		if (total > 0) {
			const creditApplicable = ((total / 100) * 10).toFixed(2);
			return creditsAvailable <= creditApplicable ? creditsAvailable : creditApplicable;
		}
		return 0;
	}
);

// Mirrors the credit value displayed in the price details: zero if the user
// explicitly chose a promo code (mutually exclusive with credits), otherwise the
// 10%-capped credit amount.
export const getCreditSavingsAmount = createSelector(
	[getCouponType, calculateCreditBasedOnQuoteBase],
	(couponType, cappedCredit = 0) => {
		if (couponType === PAYMENT_COUPON_TYPE.PROMOCODE) {
			return 0;
		}
		return Number(cappedCredit) || 0;
	}
);

/**
 * Calcule le total du panier de le page quote en tenant compte de :
 * - supplement chambre
 * - supplement pension
 * - supplement vol
 * - supplement transfert
 * - supplement activites
 * - supplement bagages
 * - supplement bagages cabine
 * - supplement flex
 * C'est le total envoyé à la requete de prebook
 */
export const calculatePreBookTotal = createSelector(
	[calculateQuoteBaseAndExtrasTotal, calculateFlexExtra],
	(total = 0, flexExtra = 0) => {
		return total + flexExtra;
	}
);

/**
 * Calcule le total du panier en tenant compte de :
 * - supplement chambre
 * - supplement pension
 * - supplement vol
 * - supplement transfert
 * - supplement activites
 * - supplement bagages
 * - reduction code promo
 * - reduction credits
 * - reduction credits
 * - supplement assurances [new]
 * Ce total s'entend hors frais de financement et est utilisé pour calculer les coûts de crédits [new]
 */
export const getQuoteTotal = createSelector(
	[calculatePreBookTotal, getInsurance, getAllCouponsTotal],
	(preBookTotal = 0, insurance = {}, allCouponsAmount = 0) => {
		const total = preBookTotal + (insurance.price || 0) - allCouponsAmount;
		return total > 0 ? total : 0;
	}
);

/**
 * Calcule le total du panier en tenant compte de :
 * - supplement chambre
 * - supplement pension
 * - supplement vol
 * - supplement transfert
 * - supplement activites
 * - supplement bagages
 * - reduction code promo
 * - reduction credits
 * - reduction avoirs
 * - supplement assurances
 * - éventuel écart de prix entre quote et paiement [new]
 * Ce total s'entend hors frais de financement et est utilisé pour calculer les coûts de crédits [new]
 */
export const getQuoteTotalWithDelta = createSelector(
	[calculatePreBookTotal, getInsurance, getAllCouponsTotal, getPaymentDeltaBasePrice],
	(preBookTotal = 0, insurance = {}, allCouponsAmount = 0, deltaBasePrice = 0) => {
		const total = preBookTotal + (insurance.price || 0) + deltaBasePrice - allCouponsAmount;
		return total > 0 ? total : 0;
	}
);

export const getCallCenter = state => state.callCenter;

export const getContextCallCenter = ({ isPaymentPage, isQuotationPage, isConfirmationPage }) =>
	createSelector(
		[getQuoteTotal, getQuoteTotalWithDelta, getCallCenter],
		(totalAvecFrais, totalAvecFraisEtDelta, callCenter) => {
			const showContextCallCenter = isPaymentPage || isQuotationPage;
			const total = isPaymentPage ? totalAvecFraisEtDelta : totalAvecFrais;

			if (
				isConfirmationPage &&
				(callCenter?.services?.servicesDisplayPhone ||
					callCenter?.services?.servicesDisplayInternationalPhone)
			) {
				return {
					...callCenter.officeHours,
					...callCenter.callPrice,
					...{
						dialPhone: callCenter?.services?.servicesDialPhone,
						displayPhone: callCenter?.services?.servicesDisplayPhone,
						displayInternationalPhone:
							callCenter?.services?.servicesDisplayInternationalPhone,
						internationalDialPhone:
							callCenter?.services?.servicesInternationalDialPhone,
						officeHours: callCenter?.services?.officeHours,
					},
				};
			}
			const contextCallCenter = showContextCallCenter
				? (get(callCenter, "contextPhones") &&
						get(callCenter, "contextPhones").find(context => {
							let contextFound = true;
							if (context.minPrice && total < context.minPrice) {
								contextFound = false;
							}
							if (context.maxPrice && total > context.maxPrice) {
								contextFound = false;
							}
							return contextFound;
						})) ||
					{}
				: get(callCenter, "contextPhones[0]") || {};
			// eslint-disable-next-line no-unused-vars
			const { contextPhones, ...mixedCallCenter } = {
				...callCenter,
				...contextCallCenter,
			};
			return mixedCallCenter;
		}
	);

export const getMaxLuggageCount = createSelector(
	[getAdults, getChildren],
	(adultsCount = 0, childrenCount) => {
		return adultsCount + childrenCount;
	}
);

export const getTravellersTotalCount = createSelector(
	[getAdults, getChildren, getBabies],
	(adultsCount = 0, childrenCount = 0, babyCount = 0) => {
		return adultsCount + childrenCount + babyCount;
	}
);

export const getMaxLuggageCountForOccupancies = createSelector(
	[getOccupancies],
	(occupancies = []) => {
		let total = 0;
		occupancies.forEach(occupancy => {
			total += occupancy.adults + occupancy.children;
		});
		return total;
	}
);

export const getSelectedTransportType = createSelector([getFlight], (flight = {}) => {
	return flight.type;
});

export const getAccommodations = createSelector(
	[getDistributions, getBooking, (_, isHotelOnly) => isHotelOnly],
	(distributions = [], booking = {}, isHotelOnly) => {
		if (isHotelOnly) {
			const distributionsWithSelectedBoards = distributions
				?.map((distribution, index) => ({
					...distribution,
					rooms: distribution?.rooms
						?.map(room => ({
							...room,
							boards: room?.boards?.filter(
								board =>
									Object.values(booking?.boardIdsByDistribution)[index] ===
									board?.id
							),
						}))
						.filter(room => room.boards?.length > 0),
				}))
				.filter(distribution => distribution.rooms?.length > 0);

			return distributionsWithSelectedBoards.map(distribution => {
				const room = distribution?.rooms?.[0] || {};
				const board = room?.boards?.[0] || {};

				return {
					accommodationType: room?.type,
					boardType: board?.type,
					freeCancellationDeadline: board?.freeCancellationDeadline || null,
					distribution: {
						adults: distribution?.occupancy?.adults,
						children: distribution?.occupancy?.children,
						babies: distribution?.occupancy?.babies,
					},
				};
			});
		}

		return booking?.accommodation?.items?.map(accommodation => ({
			accommodationType: accommodation?.label,
			boardType: booking?.board?.label,
			freeCancellationDeadline: null, // Waiting for Tourop to add freeCancellationDeadline to SDP & PP
			distribution: {
				adults: accommodation?.adults,
				children: accommodation?.children,
				babies: accommodation?.infants,
			},
		}));
	}
);

export const getArrivalAirportIata = createSelector([getFlightOutbound], (flightOutbound = {}) => {
	const outBoundLegs = flightOutbound?.legs;
	return outBoundLegs?.[outBoundLegs?.length - 1]?.to?.iata;
});

export const getDepartureAirportIata = createSelector(
	[getFlightOutbound],
	(flightOutbound = {}) => {
		return flightOutbound?.legs?.[0]?.from?.iata;
	}
);

export const getSelectedBaggageList = createSelector(
	[
		getFlightOptionsOfSelectedFlight,
		getLuggage,
		getCabinLuggage,
		getMaxLuggageCount,
		getLuggageTotalPrice,
		getLuggageTotalPublicPrice,
		getCabinLuggageTotalPrice,
		getCurrentLuggageOption,
		getSelectedTransportType,
		getBookingOfferType,
	],
	(
		flightOptions = {},
		luggage = {},
		cabinLuggage = {},
		maxLuggageCount = 0,
		luggageTotalPrice = 0,
		luggagePublicTotalPrice = 0,
		cabinLuggageTotalPrice = 0,
		currentLuggageOption = {},
		selectedTransportType,
		offerType
	) => {
		// Retourner null si c'est une offre hébergement seulement
		if (offerType === OFFER_TYPES.ACCOMODATION_ONLY) {
			return null;
		}

		const isSelectedTransportTrain = selectedTransportType === TRANSPORTATION_TYPES.TRAIN;

		// Variables communes pour les bagages
		const isHandBaggageIncluded =
			flightOptions?.flightBagageOption?.handBaggage?.included || false;
		const handBaggageDetails = flightOptions?.flightBagageOption?.handBaggage?.details;
		const isCabinBaggageIncluded =
			flightOptions?.flightBagageOption?.cabinBaggage?.included || false;
		const cabinBaggageDetails = flightOptions?.flightBagageOption?.cabinBaggage?.details;
		const isCheckedBaggageIncluded = flightOptions?.flightBagageOption?.included || false;
		const checkedBaggageQuantity =
			luggage?.quantity || flightOptions?.flightBagageOption?.quantity || 0;
		const checkedBaggageMaxWeight =
			luggage?.maxWeightPerBaggage ||
			flightOptions?.flightBagageOption?.maxWeightPerBaggage ||
			0;
		const isCheckedBaggageOffered = currentLuggageOption?.pricePerBooking === 0;

		// Calculer les détails du poids max pour les bagages en soute
		const checkedBaggageMaxWeightDetails =
			checkedBaggageMaxWeight > 0
				? {
						id: "quotation.transport.luggage.option.max.weight",
						values: { weight: checkedBaggageMaxWeight },
					}
				: null;

		if (isSelectedTransportTrain) {
			return [
				{
					type: BAGGAGE_TYPES.TRAIN_BAGGAGE,
					quantity: checkedBaggageQuantity,
					included: isCheckedBaggageIncluded,
					offered: isCheckedBaggageOffered,
					price: luggageTotalPrice,
					details: checkedBaggageMaxWeightDetails,
				},
			];
		}

		// Pour les vols
		const cabinBaggageQuantity = isCabinBaggageIncluded
			? maxLuggageCount
			: cabinLuggage?.quantity || 0;
		const isCabinBaggageOffered =
			(cabinLuggage?.quantity || 0) > 0 && cabinLuggageTotalPrice === 0;

		return [
			{
				type: BAGGAGE_TYPES.HAND_BAGGAGE,
				quantity: isHandBaggageIncluded ? maxLuggageCount : 0, // mettre à 0 si pas inclus pour masquer l'info bagage à main
				included: isHandBaggageIncluded,
				details: handBaggageDetails,
			},
			{
				type: BAGGAGE_TYPES.CABIN_BAGGAGE,
				quantity: cabinBaggageQuantity,
				included: isCabinBaggageIncluded,
				offered: isCabinBaggageOffered,
				price: cabinLuggageTotalPrice,
				details: cabinBaggageDetails,
			},
			{
				type: BAGGAGE_TYPES.CHECKED_BAGGAGE,
				quantity: checkedBaggageQuantity,
				included: isCheckedBaggageIncluded,
				offered: isCheckedBaggageOffered,
				price: luggageTotalPrice,
				publicPrice: luggagePublicTotalPrice,
				details: checkedBaggageMaxWeightDetails,
			},
		].filter(baggage => baggage.included || baggage.quantity > 0);
	}
);

export const getSavingsData = createSelector(
	[
		getBoard,
		getHideDiscountHBX,
		getSelectedBaggageList,
		getCreditSavingsAmount,
		getPromotionAmount,
		getPromotionCode,
	],
	(
		board = {},
		hideDiscountHBX,
		selectedBaggageList = [],
		creditAmount = 0,
		promotionAmount = 0,
		promotionCode = ""
	) => {
		const savingsList = {
			roomBoard: {},
			baggages: {},
			credits: {},
			promotion: {},
		};

		let amountSaved = 0;

		const boardDiscount = Number(board?.discount) || 0;

		// Board savings
		if (boardDiscount > 0 && !hideDiscountHBX) {
			amountSaved += boardDiscount;
			savingsList.roomBoard = {
				amount: boardDiscount,
			};
		}

		// Baggage savings for each baggage type
		if (selectedBaggageList?.length > 0) {
			selectedBaggageList.forEach(baggage => {
				const discountedAmount = (baggage.publicPrice || 0) - (baggage.price || 0);

				if (discountedAmount > 0) {
					amountSaved += discountedAmount;

					savingsList.baggages[baggage.type] = {
						amount: discountedAmount,
					};
				}
			});
		}

		if (creditAmount > 0) {
			amountSaved += creditAmount;
			savingsList.credits = {
				amount: creditAmount,
			};
		}

		// Promotion code savings
		if (promotionAmount > 0) {
			amountSaved += promotionAmount;
			savingsList.promotion = {
				amount: promotionAmount,
				code: promotionCode,
			};
		}

		return {
			savingsList,
			amountSaved,
		};
	}
);

export const getSavingsDataHotelOnly = createSelector(
	[getBoardByDistribution, getCreditSavingsAmount, getPromotionAmount, getPromotionCode],
	(boardByDistribution = {}, creditAmount = 0, promotionAmount = 0, promotionCode = "") => {
		const savingsList = {
			roomBoard: {},
			baggages: {},
			credits: {},
			promotion: {},
		};

		let amountSaved = 0;

		// Room board savings: sum of the discounts of the selected board of each distribution
		const roomBoardAmount = Object.values(boardByDistribution || {}).reduce((total, board) => {
			const initialPricePerStay = Number(board?.initialPricePerStay) || 0;
			const finalPricePerStayWithoutTaxes = Number(board?.finalPricePerStayWithoutTaxes) || 0;
			const boardSaving = initialPricePerStay - finalPricePerStayWithoutTaxes;

			if (boardSaving > 0) {
				return total + boardSaving;
			}

			return total;
		}, 0);

		if (roomBoardAmount > 0) {
			amountSaved += roomBoardAmount;
			savingsList.roomBoard = {
				amount: roomBoardAmount,
			};
		}

		if (creditAmount > 0) {
			amountSaved += creditAmount;
			savingsList.credits = {
				amount: creditAmount,
			};
		}

		// Promotion code savings
		if (promotionAmount > 0) {
			amountSaved += promotionAmount;
			savingsList.promotion = {
				amount: promotionAmount,
				code: promotionCode,
			};
		}

		return {
			savingsList,
			amountSaved,
		};
	}
);
